"use client"

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react"

// Bridges hovered-band visuals (band-highlight image + "High Risk" /
// "SUPERHUMAN" danger label) from the graph's SVG overlay
// (RedAboveLossOfControlOverlay) up to a sibling layer painting ABOVE
// the globe.
//
// Why: the overlay renders in the graph's SVG, whose stacking context
// is BELOW the globe, so it can't paint over it. The old fix portaled
// to document.body (position:fixed) and re-measured screen position
// every frame, lagging ~1 frame on scroll. Instead the overlay
// *publishes* payloads here in graph-local coords (no DOM measurement),
// and AboveGlobeBandHudOverlay (a plain in-flow sibling of the globe,
// pinned to the graph's box) renders them — in lockstep, zero JS.

// Highlight image + danger label live in graph-local coords (same
// space as the overlay SVG; origin = graph container top-left). The
// above-globe layer is pinned to that box, so coords map directly to
// its left/top with no getBoundingClientRect.
export type BandHighlightPayload = {
  imageHref: string
  // Raster bounds in graph-local coordinates (left/top/width/height).
  left: number
  top: number
  width: number
  height: number
  opacity: number
  // CSS gaussian blur (px) softening tile-quantized edges. 0 = off.
  featherBlurPx: number
  // Fade opacity in when a new highlight image appears. 0 = disabled.
  fadeInMs: number
}

export type BandDangerLabelPayload = {
  // Left-edge center of the hovered band, in graph-local coordinates.
  anchor: { x: number; y: number }
  text: string
  opacity: number
}

// Point-on-line hover tooltip ("…RESEARCHER / 29x speedup to AI R&D").
// Coords are graph-local. The visible box paints above the globe; the
// transparent hover hitboxes driving it stay in the graph (below the
// globe) — they only catch the mouse, not be seen.
export type PointHoverTooltipPayload = {
  // Hovered point center, in graph-local coordinates.
  cx: number
  cy: number
  // The hovered point's calendar year — the zone-clarity stacked panel (tab
  // 69) keys its live risk line off it while the LINE zone is hovered.
  year?: number
  capabilityThresholdLabel: string
  speedupText: string
  riskLabel: string
  // The takeover-risk fraction (0..1) at the hovered point — the field's
  // shading AT the line. NOTE: this HUD's band is stylized to hug the line,
  // so this reads artificially low — risk stats should use
  // currentLevelRiskAt(capabilityValue, …) instead.
  riskFraction?: number
  // The hovered point's raw capability (speedup) value — the combined
  // hover's current-level risk stat derives from it.
  capabilityValue?: number
  textColor: string
  // Vertical dotted guide line from below the point toward the axis.
  guideLineTop: number
  guideLineHeight: number
  // The guide line wears the TED guide's ORIGINAL treatment — 2px-dash/3px-
  // gap at full strength — instead of the stock dotted 35% (the matched-
  // guides tab; see POINT_GUIDE_TED_DASH_STYLE in GraphPointHoverTooltip).
  guideTedDash?: boolean
  // The hovered point sits above the dither clip (clipLinesToDitherTop) —
  // the on-line dot is not drawn; the guide line starts at the field's edge.
  dotHidden?: boolean
  // Footer advertising a click affordance; only set by interactive graphs.
  detailsHint?: string
}

// The tab-26 TED guide's payload (riskTedGuide in types.ts) — published by
// the graph so the guide can paint in the ABOVE-GLOBE layer: the line and
// label sit on top of the earth when the cursor's column passes behind it
// (hovering the earth itself ends the graph hover via the pointer gate, so
// the guide never fights the datacenter hover). Graph-local coordinates.
export type RiskTedGuidePayload = {
  x: number
  tedY: number
  year: number
  width: number
  height: number
}

// The risk-space explorer's ABOVE-GLOBE payload (picks tab 6,
// riskSpaceExplorer: "timeline-guide-above-globe") — the square, readout,
// drop line, and red axis year publish here so they paint on top of the
// earth instead of under it. Graph-local coordinates.
export type RiskSpaceGuidePayload = {
  x: number
  y: number
  levelLabel: string
  riskFraction: number
  year: number
  width: number
  height: number
}

type BandHudOverlayValue = {
  highlight: BandHighlightPayload | null
  label: BandDangerLabelPayload | null
  pointTooltip: PointHoverTooltipPayload | null
  // True while the pointer is in the takeover-risk region ABOVE the
  // capability line (see the graph's takeoverRiskAreaHover render prop) —
  // the HUD's "AI Takeover Risk" takeover reads this. Mutually exclusive
  // with pointTooltip: on/below the line the point hover wins.
  takeoverRiskAreaHover: boolean
  // The (fractional) year under the pointer while the risk field is
  // hovered — the cursor's x drives the risk takeover's "current year" the
  // way the point hover drives the capability ladder. Null when the field
  // isn't hovered.
  riskAreaHoverYear: number | null
  // The 2D risk-space explorer's live readout (riskSpaceExplorer in
  // types.ts): the capability level at the cursor's HEIGHT and the
  // authored risk for reaching that level in the cursor's year. Null
  // unless the explorer is on and the field is hovered.
  riskSpaceReadout: { levelLabel: string; riskFraction: number } | null
  // See RiskTedGuidePayload above. Null unless the TED guide is on and the
  // field is hovered.
  riskTedGuide: RiskTedGuidePayload | null
  // See RiskSpaceGuidePayload above. Null unless the explorer's above-globe
  // mode is on and the field is hovered.
  riskSpaceGuide: RiskSpaceGuidePayload | null
}

type BandHudOverlayContextValue = {
  value: BandHudOverlayValue
  setHighlight: (highlight: BandHighlightPayload | null) => void
  setLabel: (label: BandDangerLabelPayload | null) => void
  setPointTooltip: (pointTooltip: PointHoverTooltipPayload | null) => void
  setTakeoverRiskAreaHover: (hovered: boolean) => void
  setRiskAreaHoverYear: (year: number | null) => void
  setRiskSpaceReadout: (
    readout: { levelLabel: string; riskFraction: number } | null,
  ) => void
  setRiskTedGuide: (payload: RiskTedGuidePayload | null) => void
  setRiskSpaceGuide: (payload: RiskSpaceGuidePayload | null) => void
}

const FALLBACK_VALUE: BandHudOverlayValue = {
  highlight: null,
  label: null,
  pointTooltip: null,
  takeoverRiskAreaHover: false,
  riskAreaHoverYear: null,
  riskSpaceReadout: null,
  riskTedGuide: null,
  riskSpaceGuide: null,
}

const BandHudOverlayContext = createContext<BandHudOverlayContextValue | null>(
  null,
)

export const BandHudOverlayProvider = ({ children }: { children: ReactNode }) => {
  const [highlight, setHighlight] = useState<BandHighlightPayload | null>(null)
  const [label, setLabel] = useState<BandDangerLabelPayload | null>(null)
  const [pointTooltip, setPointTooltip] =
    useState<PointHoverTooltipPayload | null>(null)
  const [takeoverRiskAreaHover, setTakeoverRiskAreaHover] = useState(false)
  const [riskAreaHoverYear, setRiskAreaHoverYear] = useState<number | null>(null)
  const [riskSpaceReadout, setRiskSpaceReadout] = useState<{
    levelLabel: string
    riskFraction: number
  } | null>(null)
  const [riskTedGuide, setRiskTedGuide] = useState<RiskTedGuidePayload | null>(null)
  const [riskSpaceGuide, setRiskSpaceGuide] = useState<RiskSpaceGuidePayload | null>(null)
  const contextValue = useMemo<BandHudOverlayContextValue>(
    () => ({
      value: {
        highlight,
        label,
        pointTooltip,
        takeoverRiskAreaHover,
        riskAreaHoverYear,
        riskSpaceReadout,
        riskTedGuide,
        riskSpaceGuide,
      },
      setHighlight,
      setLabel,
      setPointTooltip,
      setTakeoverRiskAreaHover,
      setRiskAreaHoverYear,
      setRiskSpaceReadout,
      setRiskTedGuide,
      setRiskSpaceGuide,
    }),
    [highlight, label, pointTooltip, takeoverRiskAreaHover, riskAreaHoverYear, riskSpaceReadout, riskTedGuide, riskSpaceGuide],
  )
  return (
    <BandHudOverlayContext.Provider value={contextValue}>
      {children}
    </BandHudOverlayContext.Provider>
  )
}

// Publisher hooks for the overlay. They no-op when no provider is
// mounted (e.g. graph outside the HUD globe/graph pairing), so the
// overlay can always call them unconditionally.
export const useBandHudOverlayPublisher = () => {
  const ctx = useContext(BandHudOverlayContext)
  return {
    setHighlight: ctx?.setHighlight ?? noop,
    setLabel: ctx?.setLabel ?? noop,
    setPointTooltip: ctx?.setPointTooltip ?? noop,
    setTakeoverRiskAreaHover: ctx?.setTakeoverRiskAreaHover ?? noop,
    setRiskAreaHoverYear: ctx?.setRiskAreaHoverYear ?? noop,
    setRiskSpaceReadout: ctx?.setRiskSpaceReadout ?? noop,
    setRiskTedGuide: ctx?.setRiskTedGuide ?? noop,
    setRiskSpaceGuide: ctx?.setRiskSpaceGuide ?? noop,
    hasChannel: ctx !== null,
  }
}

// Reader hook used by the above-globe layer.
export const useBandHudOverlay = (): BandHudOverlayValue =>
  useContext(BandHudOverlayContext)?.value ?? FALLBACK_VALUE

const noop = () => {}
